import React, { useState } from "react";
import { Skeleton } from "@vibe/core";
import { useSlug } from "../../hooks/context/useSlug";
import ErrorPage from "../errorPage";
import UsageBoardCard from "./UsageBoardCard";
import UsageTablePagination from "./UsageTablePagination";
import useDetailedUsageTable from "../../hooks/api/useDetailedUsageTable";

const ITEMS_PER_PAGE = 10;

const DetailedUsageTable = () => {
  const { slug } = useSlug();
  const {
    activeBoards,
    isLoading: loading,
    error,
    mutateDetailedUsage,
  } = useDetailedUsageTable();
  const [expandedRows, setExpandedRows] = useState([]);
  const [page, setPage] = useState(1);

  const tableData = activeBoards.map((board) => ({
    key: board.boardId,
    boardId: board.boardId,
    boardName: board.boardName,
    itemsCount: board.items.length,
    items: board.items,
  }));

  const handleNextPage = () => {
    setPage((currentPage) => Math.min(currentPage + 1, totalPages));
  };

  const handlePreviousPage = () => {
    setPage((currentPage) => Math.max(currentPage - 1, 1));
  };

  const handleToggle = (boardId) => {
    setExpandedRows((current) => {
      if (current.includes(boardId)) {
        return current.filter((id) => id !== boardId);
      } else {
        return [...current, boardId];
      }
    });
  };

  if (error) {
    return <ErrorPage revalidate={mutateDetailedUsage} />;
  }

  const indexOfLastItem = page * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = tableData.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(tableData.length / ITEMS_PER_PAGE);

  return (
    <div>
      <div className="flex flex-col items-start">
        <div className="w-full">
          <div
            data-testid="detailed-usage-pagination-toolbar"
            className="flex sticky top-0 z-30 gap-3 justify-between items-center py-2 w-full max-w-3xl text-sm bg-white shadow-[0_-16px_0_0_#fff]"
          >
            <span>
              {Math.min(indexOfLastItem, tableData.length)} out of {tableData.length} boards
            </span>
            <UsageTablePagination
              currentPage={page}
              totalPages={totalPages}
              isLoading={loading}
              onNext={handleNextPage}
              onPrevious={handlePreviousPage}
            />
          </div>
          <div
            data-testid="detailed-usage-list-container"
            className="overflow-hidden w-full max-w-3xl bg-white rounded-xl border border-gray-200 border-solid border-y-1 border-x-1"
          >
            {loading ? (
              <div className="px-6 py-4 space-y-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} height={48} type="text" size="p" width={500} />
                ))}
              </div>
            ) : (
              currentItems.map((board, index) => {
                const isVisible = expandedRows.includes(board.boardId);
                return (
                  <UsageBoardCard
                    key={board.boardId}
                    index={index}
                    boardId={board.boardId}
                    boardName={board.boardName}
                    items={board.items}
                    slug={slug}
                    isExpanded={isVisible}
                    onToggle={handleToggle}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedUsageTable;
